import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [announcement, setAnnouncement] = useState(null);
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [userRegistrations, setUserRegistrations] = useState([]);

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      fetchAnnouncement();
      fetchAds();
      fetchUserRegistrations();
    }
  }, [token]);

  useEffect(() => {
    if (ads.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % ads.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [ads]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(`${API}/announcements/active`);
      if (response.data) {
        setAnnouncement(response.data);
      }
    } catch (error) {
      console.error('Error fetching announcement:', error);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads/active`);
      setAds(response.data || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  const fetchUserRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/registrations/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRegistrations(response.data || []);
    } catch (error) {
      console.error('Error fetching user registrations:', error);
    }
  };

  const handleRegistrationClick = () => {
    if (userRegistrations.length > 0) {
      // User sudah punya registrasi, cari yang validated untuk My Team
      const validatedRegistration = userRegistrations.find(reg => reg.status === 'validated');
      if (validatedRegistration) {
        navigate(`/berkas/${validatedRegistration.id}`);
      } else {
        // Jika tidak ada yang validated, tampilkan list registrasi atau ke notifications
        navigate('/notifications');
      }
    } else {
      // User belum registrasi, ke halaman events
      navigate('/events');
    }
  };

  const getRegistrationButtonProps = () => {
    if (userRegistrations.length > 0) {
      return {
        label: "My Team",
        icon: (
          <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      };
    } else {
      return {
        label: "Registrasi",
        icon: (
          <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )
      };
    }
  };

  const buttonProps = getRegistrationButtonProps();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-ltb-br relative pb-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bc6efd8f-02f7-4fdc-a397-f7bf259d1836/artifacts/uov20vz1_Pattern%20LTB.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header - Setelah Login */}
        <header className="bg-white shadow-md py-3 sm:py-4 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="https://twdhwrrzphgfksbrcsfl.supabase.co/storage/v1/object/public/logo/logoLTB.png" 
                alt="LTB+" 
                className="h-8 sm:h-12"
              />
            </div>
            <div className="flex items-center space-x-1 sm:space-x-3">
              {/* Admin Panel Button (only for ADMIN role) */}
              {user?.role === 'ADMIN' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-lg font-semibold transition-all flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="hidden sm:inline">Admin Panel</span>
                </button>
              )}
              
              {/* Notifications Button */}
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-2 sm:p-3 hover:bg-gray-100 rounded-lg transition-all"
                title="Notifikasi"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="relative p-2 sm:p-3 hover:bg-gray-100 rounded-lg transition-all"
                title="Logout"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
          {/* User Greeting with Token Balance */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Selamat Datang, {user?.nama}!</h2>
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                  <span className="font-bold">{user?.tokens_balance || 0}</span>
                  <span className="text-sm opacity-80">Token</span>
                </div>
                <button
                  onClick={() => navigate('/topup')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-xl font-semibold transition-all flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="hidden sm:inline">Topup</span>
                </button>
              </div>
            </div>
          </div>

          {/* Announcement Banner */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl mb-4 sm:mb-6 overflow-hidden">
            {announcement ? (
              <div className="relative">
                <img 
                  src={announcement.gambar_mobile && window.innerWidth < 768 ? announcement.gambar_mobile : announcement.gambar_url} 
                  alt="Announcement" 
                  className="w-full h-40 sm:h-64 object-cover"
                />
              </div>
            ) : (
              <div className="bg-gradient-ltb text-white py-8 sm:py-16 px-4 sm:px-8 text-center">
                <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4">Announcement</h1>
                <p className="text-base sm:text-xl">(Belum ada announcement)</p>
              </div>
            )}
          </div>

          {/* Menu Grid */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {/* Registrasi / My Team */}
              <button
                onClick={handleRegistrationClick}
                className="bg-white border-2 border-ltb-medium text-ltb-medium rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center hover:bg-ltb-medium hover:text-white transition-all shadow-md min-h-[80px] sm:min-h-[100px]"
              >
                {buttonProps.icon}
                <span className="font-bold text-xs sm:text-sm mt-1">{buttonProps.label}</span>
              </button>

              {/* Video */}
              <button 
                onClick={() => navigate('/video')}
                className="bg-white border-2 border-ltb-medium text-ltb-medium rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center hover:bg-ltb-medium hover:text-white transition-all shadow-md min-h-[80px] sm:min-h-[100px]"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="font-bold text-xs sm:text-sm mt-1">Video</span>
              </button>

              {/* Pertandingan - hidden */}
              {/* Statistik - hidden */}

              {/* Berita */}
              <button
                onClick={() => navigate('/berita')}
                className="bg-white border-2 border-ltb-medium text-ltb-medium rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center hover:bg-ltb-medium hover:text-white transition-all shadow-md min-h-[80px] sm:min-h-[100px]"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                <span className="font-bold text-xs sm:text-sm mt-1">Berita</span>
              </button>

              {/* Galeri */}
              <button 
                onClick={() => navigate('/gallery')}
                className="bg-white border-2 border-ltb-medium text-ltb-medium rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center hover:bg-ltb-medium hover:text-white transition-all shadow-md min-h-[80px] sm:min-h-[100px]"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-bold text-xs sm:text-sm mt-1">Galeri</span>
              </button>

              {/* Tiket */}
              <button 
                onClick={() => navigate('/tickets')}
                data-testid="btn-tickets"
                className="bg-white border-2 border-ltb-medium text-ltb-medium rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center hover:bg-ltb-medium hover:text-white shadow-md min-h-[80px] sm:min-h-[100px] transition-all"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                <span className="font-bold text-xs sm:text-sm mt-1">Tiket</span>
              </button>

              {/* Bantuan */}
              <button 
                onClick={() => navigate('/faq')}
                className="bg-white border-2 border-ltb-medium text-ltb-medium rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center hover:bg-ltb-medium hover:text-white shadow-md min-h-[80px] sm:min-h-[100px] transition-all"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-bold text-xs sm:text-sm mt-1">Bantuan</span>
              </button>
            </div>
          </div>

          {/* Ads Carousel */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 mb-4 sm:mb-6">
            {ads.length > 0 ? (
              <>
                <div className="bg-gray-100 rounded-lg sm:rounded-xl h-32 sm:h-48 overflow-hidden">
                  {ads[currentAdIndex].link ? (
                    <a 
                      href={ads[currentAdIndex].link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block w-full h-full cursor-pointer hover:opacity-90 transition-opacity"
                    >
                      <img 
                        src={ads[currentAdIndex].gambar_mobile && window.innerWidth < 768 ? ads[currentAdIndex].gambar_mobile : ads[currentAdIndex].gambar_url} 
                        alt={`Ad ${currentAdIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </a>
                  ) : (
                    <img 
                      src={ads[currentAdIndex].gambar_mobile && window.innerWidth < 768 ? ads[currentAdIndex].gambar_mobile : ads[currentAdIndex].gambar_url} 
                      alt={`Ad ${currentAdIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex justify-center mt-3 sm:mt-4 space-x-2">
                  {ads.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentAdIndex(index)}
                      className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
                        currentAdIndex === index ? 'bg-ltb-deep' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-gradient-ltb rounded-lg sm:rounded-xl h-32 sm:h-48 flex items-center justify-center text-white">
                <p className="text-lg sm:text-2xl font-bold">Belum ada iklan</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
